/**
 * Talking to our own backend.
 *
 * Same origin in production, proxied in development, so the session cookie
 * rides along without anybody thinking about CORS.
 */

export type User = { identityUserId: string; email: string; name: string; role: string };

export type Category = {
  id: string;
  name: string;
  tracking: 'tracked' | 'stock';
  icon: string;
  fieldSchema: CategoryField[];
  assetCount: number;
  stockCount: number;
};

export type Person = {
  id: string;
  identityUserId: string;
  email: string;
  name: string;
  active: boolean;
  assetCount: number;
  deviceCount: number;
};

export type CategoryField = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select';
  options?: string[];
  required?: boolean;
};

export type Location = {
  id: string;
  name: string;
  kind: string;
  notes: string;
  assetCount: number;
};

export type AssetState = 'available' | 'assigned' | 'repair' | 'lost' | 'retired';

export type Asset = {
  id: string;
  assetTag: string | null;
  serialNumber: string | null;
  name: string;
  make: string;
  model: string;
  state: AssetState;
  condition: string;
  notes: string;
  fields: Record<string, unknown>;
  purchasedOn: string | null;
  purchaseCost: number | null;
  supplier: string;
  invoiceRef: string;
  warrantyUntil: string | null;
  parentAssetId: string | null;
  parentAssetName: string | null;
  categoryId: string;
  categoryName: string;
  locationId: string | null;
  locationName: string | null;
  heldById: string | null;
  heldByName: string | null;
  heldByEmail: string | null;
  updatedAt: string;
};

export type StockItem = {
  id: string;
  name: string;
  make: string;
  model: string;
  categoryId: string;
  categoryName: string;
  locationName: string | null;
  reorderLevel: number;
  total: number;
  assigned: number;
  defective: number;
  working: number;
  available: number;
};

export type Movement = {
  id: string;
  kind: string;
  quantity: number;
  reason: string;
  notes: string;
  recordedBy: string;
  at: string;
  personName: string | null;
};

/**
 * One failure shape, carrying what the server said.
 *
 * The backend answers a refusal with a sentence meant to be read -- "there
 * are not that many to issue, 7 available" -- and throwing away that message
 * in favour of "request failed" is how a screen ends up less useful than the
 * API behind it.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), ...init.headers },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      // Not JSON. The status is all there is.
    }
    throw new ApiError(message, response.status);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
